import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import '../../modules/SettingsDetailPage.css'; // shared .sd-grid--side / .sd-input

/** GET /api/v1/emails/contacts/{id} — the editable Contact fields (ContactEditDetail). */
interface ContactEdit {
  oid: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  email: string | null;
  officePhone: string | null;
  mobilePhone: string | null;
  position: string | null;
  jobTitle: string | null;
}

type Field = Exclude<keyof ContactEdit, 'oid'>;
const FIELDS: { key: Field; label: string; type?: string }[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'middleName', label: 'Middle Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'officePhone', label: 'Office Phone' },
  { key: 'mobilePhone', label: 'Mobile Phone' },
  { key: 'position', label: 'Position' },
  { key: 'jobTitle', label: 'Job Title' },
];

/**
 * Edit one Contact — opened by the per-row EDIT PENCIL in the Email recipient (To/CC/Bcc)
 * Link picker (legacy XAF Contact_DetailView, owner request 2026-10-15, image 638). A focused
 * subset of the Contact: the name parts, the mail address + phones, and the role fields. Save
 * goes through the shared useSaveFlow ("Do you want to Save?" → audited PUT → "Saved
 * successfully.") and refreshes the picker so the change shows at once. The server audits one
 * row per changed field under FormName "Email".
 */
export function ContactEditDialog({
  contactId,
  onClose,
  onSaved,
  overlayClassName,
}: {
  contactId: string;
  onClose: () => void;
  onSaved: () => void;
  /** Lift above the picker modal (the picker is z-index 5000; AppDialog's overlay is 1500). */
  overlayClassName?: string;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [form, setForm] = useState<ContactEdit | null>(null);
  const [initial, setInitial] = useState<ContactEdit | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<{ data?: ContactEdit }>(`/api/v1/emails/contacts/${contactId}`)
      .then((r) => {
        if (!alive) return;
        const d = r.data?.data ?? null;
        setForm(d);
        setInitial(d);
      })
      .catch(() => alive && notify.error('The contact could not be loaded.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [contactId, notify]);

  // Dirty when any field differs from the loaded value (standing rule 15).
  const dirty = useMemo(() => {
    if (!form || !initial) return false;
    return FIELDS.some((f) => (form[f.key] ?? '') !== (initial[f.key] ?? ''));
  }, [form, initial]);
  useUnsavedGuard(dirty);

  const set = (k: Field, v: string) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const save = () => {
    if (!form || saving) return;
    setSaving(true);
    void runSave({
      formName: 'Email',
      auditAction: 'edit',
      save: async () => {
        await api.put(`/api/v1/emails/contacts/${contactId}`, {
          firstName: form.firstName,
          middleName: form.middleName,
          lastName: form.lastName,
          email: form.email,
          officePhone: form.officePhone,
          mobilePhone: form.mobilePhone,
          position: form.position,
          jobTitle: form.jobTitle,
        });
      },
      onSaved: () => {
        setSaving(false);
        onSaved();
        onClose();
      },
      onError: () => {
        setSaving(false);
        notify.error('The contact could not be saved.');
      },
    });
  };

  const cancel = () => {
    if (!dirty) {
      onClose();
      return;
    }
    void confirmDiscardIfUnsaved().then((ok) => {
      if (ok) onClose();
    });
  };

  return (
    <AppDialog
      open
      title="Contact"
      icon="ti-user"
      width={520}
      className={`dlg-center${overlayClassName ? ` ${overlayClassName}` : ''}`}
      onClose={cancel}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading || !form || saving} onClick={save}>
            Save
          </button>
          <button type="button" className="ca-btn" onClick={cancel}>
            Cancel
          </button>
        </>
      }
    >
      {loading || !form ? (
        <div style={{ padding: 20, color: 'var(--ai-muted, #6b7280)' }}>Loading…</div>
      ) : (
        <div className="sd-grid sd-grid--single sd-grid--side" style={{ maxWidth: 'none' }}>
          {FIELDS.map((f) => (
            <div className="sd-row" key={f.key}>
              <label className="sd-label">{f.label}</label>
              <input
                className="sd-input"
                type={f.type ?? 'text'}
                maxLength={100}
                value={String(form[f.key] ?? '')}
                onChange={(e) => set(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </AppDialog>
  );
}
